import { useRouter } from "next/router";
import { useEffect, useId, useRef, useState } from "react";

import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Icon from "@afa-shared/afa-components/dist/Icon";
import Text from "@afa-shared/afa-components/dist/Text";
import TopImage from "@afa-shared/afa-components/dist/TopImage";
import AfaBreadCrumbs from "@components/AfaBreadcrumbs";
import PhotoInfo from "@components/PhotoInfo/PhotoInfo";
import { PrintButton } from "@components/PrintButton";
import RichTextRenderer from "@components/RichTextRenderer";
import LeftMenu from "@components/navigation/LeftMenu";
import { getFactsStructureBreadCrumbs } from "@containers/InsuranceHandbookPage/utils/insuranceHandbookpageHelper";
import { defaultContext } from "@utils/contextHelper/getInitialContext";
import { getImageMetaData } from "@utils/imageHelpers/metadataImageHelper";
import { getTopImage } from "@utils/imageHelpers/topImageHelper";
import { getContentAreaMaxWidth } from "@utils/pageHelper";

import { useEscape } from "hooks/useEscape";
import useMediaQuery from "hooks/useMediaQuery";
import { Context } from "types/context/Context";

import CFContentArea from "../../components/cFContentArea";
import pageStyles from "../../styles/page.module.css";
import insuranceHandbookPageStyles from "./InsuranceHandbookPage.module.css";
import { InsuranceHandbookPageType_insuranceHandbookPage } from "./queries/contentfulTypes/InsuranceHandbookPageType";

const InsuranceHandbookPage = (props: any) => {
  const context: Context = { ...defaultContext };
  const router = useRouter();
  const [openLeftMenu, setOpenLeftMenu] = useState<boolean>(false);
  const isTablet = useMediaQuery("tablet");
  const isDesktopUp = useMediaQuery("desktopup");
  const mobileButtonRef = useRef(null);
  const labelId = useId();

  const insuranceHandbookPage: InsuranceHandbookPageType_insuranceHandbookPage = props?.pageData;
  const inheritedContentArea = props?.inheritedContentArea;

  const imageData = insuranceHandbookPage?.image?.[0];
  const image = getTopImage(imageData, 900);
  const metadata = getImageMetaData(imageData);

  useEffect(() => {
    setOpenLeftMenu(false);
  }, [router.query]);

  useEscape(
    mobileButtonRef,
    () => {
      mobileButtonRef?.current?.focus();
      setOpenLeftMenu(false);
    },
    openLeftMenu
  );

  return (
    <div className={insuranceHandbookPageStyles.insuranceHandbookPage}>
      {isTablet && (
        <div className={insuranceHandbookPageStyles.toggleButtonWrapper}>
          <button
            className={insuranceHandbookPageStyles.toggleButton}
            onClick={() => {
              setOpenLeftMenu(!openLeftMenu);
            }}
            ref={mobileButtonRef}
            aria-labelledby={labelId}
            aria-expanded={openLeftMenu}
          >
            <div>
              <Icon name={"page_menu"} size="sm" className={insuranceHandbookPageStyles.iconList} />
              <span id={labelId}>
                {openLeftMenu
                  ? "Stäng menyn för Försäkringshandboken"
                  : "Välj sida i Försäkringshandboken"}
              </span>
            </div>
            <Icon
              name={openLeftMenu ? "close" : "chevron_down"}
              size="sm"
              className={insuranceHandbookPageStyles.iconChevron}
            />
          </button>
        </div>
      )}

      {(isDesktopUp || !openLeftMenu) && (
        <AfaBreadCrumbs
          breadCrumbLinks={getFactsStructureBreadCrumbs(props?.breadcrumbs)}
          ariaLabel={"Brödsmulor"}
        />
      )}

      {(isDesktopUp || !openLeftMenu) && image && image?.url && (
        <div
          data-contentful-entry-id={insuranceHandbookPage.sys.id}
          data-contentful-field-id="image"
        >
          <div className={insuranceHandbookPageStyles.imageWrapper}>
            <TopImage imgUrl={image?.url} altText={metadata?.altText} />
          </div>
        </div>
      )}

      <div
        className={`${insuranceHandbookPageStyles.contentWrapper} ${
          openLeftMenu ? insuranceHandbookPageStyles.menuOpen : ""
        }`}
      >
        {(isDesktopUp || !openLeftMenu) && image && image?.url && (
          <Grid justifyContent="space-between" direction="row">
            <PhotoInfo
              description={insuranceHandbookPage.topImageText}
              photographer={metadata?.photographer}
              contentfulPreviewEntryId={insuranceHandbookPage.sys.id}
              contentfulPreviewImageTextFieldId="topImageText"
            />
          </Grid>
        )}

        <div
          className={`${insuranceHandbookPageStyles.wrapper} ${
            image && image?.url ? insuranceHandbookPageStyles.hasBackgroundImage : ""
          } ${openLeftMenu ? insuranceHandbookPageStyles.menuOpen : ""}`}
        >
          <Grid
            mobile={12}
            desktop={4}
            desktopLarge={3}
            className={`${insuranceHandbookPageStyles.menuGrid} ${
              openLeftMenu ? insuranceHandbookPageStyles.menuOpen : ""
            }`}
          >
            <LeftMenu leftmenuItems={props?.leftmenuItems} />
          </Grid>

          <Grid
            mobile={12}
            desktop={8}
            desktopLarge={6}
            className={`${insuranceHandbookPageStyles.pageContent} ${
              openLeftMenu ? insuranceHandbookPageStyles.menuOpen : ""
            }`}
            id={"innehall"}
          >
            <Grid container>
              <Heading
                variant="h1"
                data-contentful-entry-id={insuranceHandbookPage.sys.id}
                data-contentful-field-id="heading"
              >
                {insuranceHandbookPage?.heading}
              </Heading>
              {insuranceHandbookPage?.preamble && (
                <Text
                  variant={"preamble"}
                  children={insuranceHandbookPage?.preamble}
                  data-contentful-entry-id={insuranceHandbookPage.sys.id}
                  data-contentful-field-id="preamble"
                />
              )}
            </Grid>
            <div className={pageStyles.contentAreaGrid}>
              {insuranceHandbookPage?.mainbody?.json && (
                <div
                  data-contentful-entry-id={insuranceHandbookPage.sys.id}
                  data-contentful-field-id="mainBody"
                >
                  <RichTextRenderer
                    json={insuranceHandbookPage?.mainbody?.json}
                    links={insuranceHandbookPage?.mainbody?.links}
                    pageTextArea
                  />
                </div>
              )}
              {insuranceHandbookPage?.mainAreaCollection?.items?.length > 0 && (
                <CFContentArea
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  contentAreaItems={insuranceHandbookPage?.mainAreaCollection?.items}
                  maxWidth={getContentAreaMaxWidth(8)}
                  context={context}
                />
              )}
              {insuranceHandbookPage?.mainBodyBottom?.json && (
                <div
                  data-contentful-entry-id={insuranceHandbookPage.sys.id}
                  data-contentful-field-id="mainBodyBottom"
                >
                  <RichTextRenderer
                    json={insuranceHandbookPage?.mainBodyBottom?.json}
                    links={insuranceHandbookPage?.mainBodyBottom?.links}
                    pageTextArea
                  />
                </div>
              )}
              {insuranceHandbookPage?.mainAreaBottomCollection?.items?.length > 0 && (
                <CFContentArea
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  contentAreaItems={insuranceHandbookPage?.mainAreaBottomCollection?.items}
                  maxWidth={getContentAreaMaxWidth(8)}
                  context={context}
                />
              )}
            </div>
            <PrintButton />
          </Grid>

          {inheritedContentArea?.items &&
            inheritedContentArea?.items?.length > 0 &&
            (isDesktopUp || !openLeftMenu) && (
              <Grid className={"inheritedContentAreaWrapper"}>
                <CFContentArea
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  contentAreaItems={inheritedContentArea?.items}
                  heading={"h2"}
                  context={context}
                />
              </Grid>
            )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceHandbookPage;
